import { useMemo } from "react";
import { AlertTriangle, Flag } from "lucide-react";
import { cn } from "@/lib/utils";

const DAY = 86400000;
const STAGE_BAR = {
  done: "bg-[hsl(var(--halo-teal))]/70",
  active: "bg-[hsl(var(--studio-primary))]/80",
  todo: "bg-muted-foreground/25",
};

const dayFloor = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const fmt = (d) => d.toLocaleDateString(undefined, { day: "numeric", month: "short" });

/**
 * Derived Gantt: stage bars laid out sequentially from the project start using each
 * stage's target duration, with today + deadline markers and task due-date pips.
 */
export default function ProjectTimeline({ project, stages, tasks }) {
  const plan = useMemo(() => {
    const start = dayFloor(project.created_at || new Date());
    let cursor = 0;
    const bars = stages.map((s) => {
      const days = Math.max(1, s.target_duration_days || 5);
      const bar = { stage: s, offset: cursor, days };
      cursor += days;
      return bar;
    });
    const planDays = Math.max(cursor, 1);
    const deadline = project.deadline ? dayFloor(project.deadline) : null;
    const deadlineDays = deadline ? Math.round((deadline - start) / DAY) : null;
    const today = dayFloor(new Date());
    const todayDays = Math.round((today - start) / DAY);
    const totalDays = Math.max(planDays, deadlineDays ?? 0, todayDays + 1, 1);
    return { start, bars, planDays, deadlineDays, todayDays, totalDays, overDeadline: deadlineDays != null && planDays > deadlineDays };
  }, [project.created_at, project.deadline, stages]);

  const pct = (days) => `${Math.min(100, Math.max(0, (days / plan.totalDays) * 100))}%`;
  const endDate = new Date(plan.start.getTime() + plan.totalDays * DAY);

  if (stages.length === 0) return null;

  return (
    <div className="space-y-4" data-testid="project-timeline">
      <div className="flex flex-wrap items-center gap-4 text-[11px] text-muted-foreground">
        <span>{fmt(plan.start)} → {fmt(endDate)}</span>
        <span className="inline-flex items-center gap-1.5"><i className="w-3 h-2 rounded-sm bg-[hsl(var(--studio-primary))]/80" /> active</span>
        <span className="inline-flex items-center gap-1.5"><i className="w-3 h-2 rounded-sm bg-[hsl(var(--halo-teal))]/70" /> done</span>
        <span className="inline-flex items-center gap-1.5"><i className="w-3 h-2 rounded-sm bg-muted-foreground/25" /> planned</span>
        <span className="inline-flex items-center gap-1.5"><i className="w-0.5 h-3 bg-[hsl(var(--studio-primary))]" /> today</span>
        {project.deadline && <span className="inline-flex items-center gap-1.5"><Flag className="w-3 h-3" /> deadline {project.deadline}</span>}
      </div>

      {plan.overDeadline && (
        <div className="flex items-start gap-2 rounded-lg border border-[hsl(var(--studio-warning)/0.40)] bg-[hsl(var(--studio-warning)/0.16)] p-3 text-xs" data-testid="timeline-deadline-warning">
          <AlertTriangle className="w-4 h-4 text-[hsl(var(--studio-warning))] shrink-0 mt-0.5" />
          <div>
            The stage plan runs <b>{plan.planDays - plan.deadlineDays} day{plan.planDays - plan.deadlineDays === 1 ? "" : "s"}</b> past the project deadline.
            Trim a stage duration or push the deadline.
          </div>
        </div>
      )}

      <div className="rounded-xl border border-border overflow-hidden">
        {plan.bars.map(({ stage, offset, days }) => {
          const stageTasks = tasks.filter((t) => t.stage_key === stage.key);
          const withDue = stageTasks.filter((t) => t.due_date);
          return (
            <div key={stage.key} className="grid grid-cols-[minmax(120px,180px)_1fr] items-center border-b border-border last:border-b-0" data-testid={`timeline-row-${stage.key}`}>
              <div className="px-3 py-3 min-w-0">
                <div className="text-sm font-medium truncate">{stage.name}</div>
                <div className="text-[11px] text-muted-foreground">
                  {days}d · {stageTasks.filter((t) => t.status === "done").length}/{stageTasks.length} tasks
                </div>
              </div>
              <div className="relative h-14 border-l border-border">
                {/* today marker */}
                {plan.todayDays >= 0 && plan.todayDays <= plan.totalDays && (
                  <div className="absolute top-0 bottom-0 w-0.5 bg-[hsl(var(--studio-primary))]" style={{ left: pct(plan.todayDays) }} />
                )}
                {/* deadline marker */}
                {plan.deadlineDays != null && (
                  <div className="absolute top-0 bottom-0 w-0.5 bg-[hsl(var(--studio-danger))]/70" style={{ left: pct(plan.deadlineDays) }} />
                )}
                {/* stage bar */}
                <div
                  className={cn("absolute top-1/2 -translate-y-1/2 h-6 rounded-md flex items-center px-2", STAGE_BAR[stage.status] || STAGE_BAR.todo)}
                  style={{ left: pct(offset), width: pct(days) }}
                  data-testid={`timeline-bar-${stage.key}`}
                  title={`${stage.name} · ${days} days`}
                >
                  <span className="text-[10px] font-medium text-background/90 truncate">{stage.status}</span>
                </div>
                {/* task due pips */}
                {withDue.map((t) => {
                  const d = Math.round((dayFloor(t.due_date) - plan.start) / DAY);
                  if (d < 0 || d > plan.totalDays) return null;
                  return (
                    <div
                      key={t.id}
                      className={cn(
                        "absolute bottom-1.5 w-2 h-2 rounded-full border border-background",
                        t.status === "done" ? "bg-[hsl(var(--halo-teal))]" : "bg-[hsl(var(--studio-danger))]"
                      )}
                      style={{ left: pct(d) }}
                      title={`${t.title} · due ${t.due_date}`}
                      data-testid={`timeline-pip-${t.id}`}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
